// Tiny IntersectionObserver hook. Fires `onIntersect` whenever the observed
// element transitions into view, throttled by the observer to one call per
// crossing. Used by the members-panel infinite-scroll sentinel; kept in
// shared/lib so future infinite lists (lifecycle, content, alerts) can reuse
// it without re-rolling.
//
// `rootMargin` defaults to "200px 0px" so the trigger fires ~200px before
// the sentinel scrolls into view — a soft prefetch that hides the network
// round-trip behind the user's scroll momentum. The TASK-032 AC says
// "trigger at 80% of viewport"; this gets the same effect with less
// scroll-position math.

import { useEffect, useRef } from "react";

export interface UseIntersectionOptions {
  enabled?: boolean;
  rootMargin?: string;
  /** Pluggable factory for tests; defaults to window.IntersectionObserver. */
  observerFactory?: (
    callback: IntersectionObserverCallback,
    init: IntersectionObserverInit,
  ) => IntersectionObserver | null;
}

export function useIntersection(
  onIntersect: () => void,
  options: UseIntersectionOptions = {},
): (node: Element | null) => void {
  const { enabled = true, rootMargin = "200px 0px", observerFactory } = options;
  const targetRef = useRef<Element | null>(null);
  const callbackRef = useRef(onIntersect);
  callbackRef.current = onIntersect;

  useEffect(() => {
    if (!enabled) return;
    const target = targetRef.current;
    if (!target) return;
    const factory =
      observerFactory ??
      ((cb: IntersectionObserverCallback, init: IntersectionObserverInit) => {
        if (typeof IntersectionObserver === "undefined") return null;
        return new IntersectionObserver(cb, init);
      });
    const observer = factory(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            callbackRef.current();
            return;
          }
        }
      },
      { rootMargin },
    );
    if (!observer) return;
    observer.observe(target);
    return () => observer.disconnect();
  }, [enabled, rootMargin, observerFactory]);

  return (node: Element | null) => {
    targetRef.current = node;
  };
}
